// Nachtrag math, client twin of utils/changeOrders/calc.js (live totals while editing; the
// server stays authoritative). Keep everything below the header identical to the server file:
// test/changeOrders/calc.test.js compares both.

const REASONS = [
  "customer_change",
  "additional_work",
  "unforeseen",
  "quantity_change",
  "omitted_work",
  "unordered_work",
  "hindrance",
  "other"
];
const CONTRACT_BASES = ["vob", "bgb", "bgb_consumer"];
const PRICING_BASES = ["unit_prices", "calculation", "hourly"];
const STATES = ["draft", "announced", "offered", "ordered", "approved", "rejected", "withdrawn"];
// States in which the Nachtrag is still waiting for the customer
const PENDING_STATES = ["announced", "offered", "ordered"];
// States in which nothing can be changed any more
const DECIDED_STATES = ["approved", "rejected", "withdrawn"];
const TAX_RATES = [19, 7, 0];
const MAX_POSITIONS = 300;
const MAX_CENTS = 100000000;
const MAX_QUANTITY = 1000000;

/** Integer a / b rounded half away from zero (b > 0). */
const divRound = (a, b) => {
  const sign = a < 0 ? -1 : 1;
  return sign * Math.floor((Math.abs(a) * 2 + b) / (2 * b));
};

/** Keeps what a position may hold. A negative quantity is work that is left out. */
const normalizePosition = (raw = {}) => {
  const p = raw || {};
  const quantity = Number(p.quantity);
  const unitPrice = Math.round(Number(p.unitPrice));
  const out = {
    description: String(p.description || "")
      .trim()
      .slice(0, 2000),
    quantity: Number.isFinite(quantity)
      ? Math.max(-MAX_QUANTITY, Math.min(MAX_QUANTITY, Math.round(quantity * 1000) / 1000))
      : 0,
    unit: String(p.unit || "")
      .trim()
      .slice(0, 20),
    unitPrice: Number.isFinite(unitPrice) ? Math.max(0, Math.min(MAX_CENTS, unitPrice)) : 0,
    // Position of the main offer whose price this one continues (§ 2 Abs. 5/6 VOB/B)
    basePosition: p.basePosition || null
  };
  if (p._id) out._id = p._id;
  return out;
};

const positionNet = position =>
  divRound(
    Math.round(Number(position.quantity || 0) * 1000) * Number(position.unitPrice || 0),
    1000
  );

/** Positions with net cents, totals and warnings. */
const summarizeChangeOrder = (changeOrder = {}) => {
  const taxRate = TAX_RATES.includes(Number(changeOrder.taxRate))
    ? Number(changeOrder.taxRate)
    : 19;
  const warnings = [];
  const positions = (changeOrder.positions || []).map((raw, index) => {
    const position = normalizePosition(raw);
    if (!position.description) warnings.push({ code: "NO_DESCRIPTION", position: index });
    if (!position.quantity) warnings.push({ code: "NO_QUANTITY", position: index });
    if (!position.unitPrice) warnings.push({ code: "NO_PRICE", position: index });
    return { ...position, net: positionNet(position) };
  });
  if (!positions.length && changeOrder.pricingBasis !== "hourly") warnings.push({ code: "EMPTY" });
  if (!String(changeOrder.cause || "").trim()) warnings.push({ code: "NO_CAUSE" });
  const net = positions.reduce((sum, p) => sum + p.net, 0);
  const tax = divRound(net * taxRate, 100);
  return { positions, taxRate, warnings, totals: { net, tax, gross: net + tax } };
};

/**
 * Contract value of a main offer with its Nachträge (net cents):
 * original = the offer's own positions, approved = merged Nachträge, pending = waiting for the
 * customer (including ordered without an agreed price), current = original + approved.
 */
const contractOverview = ({ originalNet = 0, changeOrders = [] } = {}) => {
  const sum = states =>
    changeOrders
      .filter(c => states.includes(c.state))
      .reduce((total, c) => total + Number(c.net || 0), 0);
  const approvedNet = sum(["approved"]);
  return {
    originalNet,
    approvedNet,
    pendingNet: sum(PENDING_STATES),
    draftNet: sum(["draft"]),
    rejectedNet: sum(["rejected"]),
    currentNet: originalNet + approvedNet,
    count: changeOrders.length,
    pendingCount: changeOrders.filter(c => PENDING_STATES.includes(c.state)).length
  };
};

/** "YYYY-MM-DD" responseDue is past and the customer has not decided yet. */
const isOverdue = (changeOrder, today) =>
  Boolean(
    changeOrder &&
      PENDING_STATES.includes(changeOrder.state) &&
      changeOrder.state !== "ordered" &&
      changeOrder.responseDue &&
      changeOrder.responseDue < today
  );

export {
  REASONS,
  CONTRACT_BASES,
  PRICING_BASES,
  STATES,
  PENDING_STATES,
  DECIDED_STATES,
  TAX_RATES,
  MAX_POSITIONS,
  divRound,
  normalizePosition,
  positionNet,
  summarizeChangeOrder,
  contractOverview,
  isOverdue
};
